import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, formatDateTime, formatFileSize } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Upload, RefreshCw, Trash2, CheckCircle2, Clock, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * SuperAdmin → App Android
 * Carica, visualizza e sostituisce l'APK Android distribuito agli utenti.
 */
export default function SuperAdminAndroidApp() {
  const [info, setInfo] = useState({ current: null, history: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/android-app/info");
      setInfo(data || { current: null, history: [], total: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      toast.error("Il file deve avere estensione .apk");
      e.target.value = "";
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error("APK troppo grande (max 100 MB)");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Seleziona un file APK");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({ version_label: version.trim() });
      const { data } = await api.post(`/admin/android-app/upload?${qs.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`APK caricato: ${data.filename}${data.version_label ? ` v${data.version_label}` : ""}`);
      setFile(null);
      setVersion("");
      document.getElementById("apk-file-input").value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeRelease = async (rel) => {
    if (!window.confirm(`Eliminare la versione "${rel.filename}" (${rel.version_label || "senza label"})?\n\nL'operazione cancella anche il file APK dallo storage.`)) return;
    try {
      await api.delete(`/admin/android-app/${rel.id}`);
      toast.success("Release eliminata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="superadmin-android-app">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <Smartphone className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">App Android</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Carica e distribuisci l'APK Android (WebView del portale clienti) via endpoint pubblico
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="apk-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {/* Versione corrente */}
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-5" data-testid="apk-current-card">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
          <span className="font-semibold text-emerald-900 dark:text-emerald-100">Versione corrente</span>
          {info.current?.version_label && (
            <Badge className="bg-emerald-600 text-white">v{info.current.version_label}</Badge>
          )}
        </div>
        {info.current ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <Field label="File" value={info.current.filename} testid="apk-cur-filename" />
            <Field label="Dimensione" value={formatFileSize(info.current.size || 0)} testid="apk-cur-size" />
            <Field label="Caricato il" value={formatDateTime(info.current.created_at)} testid="apk-cur-date" />
            <Field label="Da" value={info.current.uploaded_by_email || "—"} testid="apk-cur-by" />
          </div>
        ) : (
          <p className="text-sm text-emerald-800 dark:text-emerald-200">
            Nessuna versione caricata — carica il primo APK con il modulo qui sotto.
          </p>
        )}
        {info.current && (
          <div className="mt-4 p-3 rounded-lg bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-700">
            <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider mb-1">Endpoint pubblico download</div>
            <code className="text-xs break-all text-slate-800 dark:text-slate-200" data-testid="apk-download-url">
              GET /api/download/android-app
            </code>
          </div>
        )}
      </div>

      {/* Upload nuova versione */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="h-5 w-5 text-blue-600 dark:text-blue-300" />
          <span className="font-semibold text-slate-900 dark:text-slate-50">Carica nuova versione</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600 dark:text-slate-300">File APK (.apk, max 100 MB)</Label>
            <Input
              id="apk-file-input"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={onFileChange}
              className="mt-1 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
              data-testid="apk-file"
            />
            {file && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                {file.name} · {formatFileSize(file.size)}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs text-slate-600 dark:text-slate-300">Versione (opzionale)</Label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="es. 1.0.3"
              className="mt-1 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
              data-testid="apk-version"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={upload} disabled={uploading || !file} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="apk-upload">
            {uploading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? "Caricamento…" : "Carica e pubblica"}
          </Button>
        </div>
      </div>

      {/* Storico release */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <Clock className="h-5 w-5 text-slate-500 dark:text-slate-400" />
          <span className="font-semibold text-slate-900 dark:text-slate-50">Storico release</span>
          <Badge variant="outline" className="ml-auto bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">{info.total}</Badge>
        </div>
        {info.history.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">Nessuna release in archivio.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Stato</th>
                  <th className="px-4 py-2 text-left">File</th>
                  <th className="px-4 py-2 text-left">Versione</th>
                  <th className="px-4 py-2 text-right">Dimensione</th>
                  <th className="px-4 py-2 text-left">Caricato</th>
                  <th className="px-4 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {info.history.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`apk-row-${r.id}`}>
                    <td className="px-4 py-2">
                      {r.is_current ? (
                        <Badge className="bg-emerald-600 text-white">Corrente</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600">Storica</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-800 dark:text-slate-100 truncate max-w-[220px]" title={r.filename}>{r.filename}</td>
                    <td className="px-4 py-2 text-slate-700 dark:text-slate-200">{r.version_label || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-slate-600 dark:text-slate-300">{formatFileSize(r.size || 0)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {formatDateTime(r.created_at)}
                      <div className="text-[10px] text-slate-400 dark:text-slate-500">{r.uploaded_by_email || "—"}</div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeRelease(r)}
                        disabled={r.is_current && info.total === 1}
                        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/40 text-red-600 dark:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={r.is_current && info.total === 1 ? "Non puoi eliminare l'unica release" : "Elimina"}
                        data-testid={`apk-del-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-slate-900 dark:text-slate-100 truncate" data-testid={testid} title={value}>{value}</div>
    </div>
  );
}
